import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { formatDateTime, getRowsPerPageOptions } from "../../utils/functions";
import {
	Chip,
	Box,
	Typography,
	Tooltip,
	Paper,
	TableContainer,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@mui/styles";
import { getAuditLogs } from "../../utils/commonServices";
import { paginationStyle } from "../../utils/constants";

const useStyles = makeStyles((theme) => ({
	txtcolor: {
		color: theme.palette.colorFont,
		"&.MuiDataGrid-root": {
			color: theme.palette.colorFont,
		},
	},
	tableContainer: {
		overflowX: "auto",
		width: "100%",
		minWidth: 0,
	},
	paper: {
		width: "100%",
		boxSizing: "border-box",
		overflowX: "auto",
	},
}));

const INITIAL_SORT_MODEL = [{ field: "changedAt", sort: "desc" }];

// Action badge colors
const getActionColor = (action) => {
	switch (action?.toUpperCase()) {
		case "CREATE":
			return "success";
		case "UPDATE":
			return "info";
		case "DELETE":
			return "error";
		default:
			return "default";
	}
};

// Format change description from old/new values
const formatChangeDescription = (action, oldValues, newValues, tableName) => {
	try {
		if (action === "CREATE") {
			return `New ${tableName} record created`;
		}
		if (action === "DELETE") {
			return `${tableName} record deleted`;
		}

		// For UPDATE, try to show what changed
		const oldObj = oldValues ? JSON.parse(oldValues) : {};
		const newObj = newValues ? JSON.parse(newValues) : {};

		const changes = [];
		Object.keys(newObj).forEach((key) => {
			if (oldObj[key] !== newObj[key]) {
				changes.push(
					`${key}: ${oldObj[key] || "(empty)"} → ${newObj[key] || "(empty)"}`,
				);
			}
		});

		return changes.length > 0 ? changes.join("; ") : "Record updated";
	} catch (e) {
		return action === "CREATE"
			? `New ${tableName} record created`
			: action === "DELETE"
				? `${tableName} record deleted`
				: "Record updated";
	}
};

const AuditLog = () => {
	const classes = useStyles();
	const theme = useTheme();
	const dispatch = useDispatch();
	const themeMode = useSelector((state) => state.theme.mode);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);

	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

	const computedHeight = isTablet
		? "calc(100vh - 180px)"
		: isSmall
			? "calc(100vh - 220px)"
			: "calc(100vh - 180px)";

	// Local state for audit log data
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);

	// Table state
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(20);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState({});
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [scrollHeight, setScrollHeight] = useState(window.innerHeight - 280);
	const [tableHeight, setTableHeight] = useState(window.innerHeight - 260);

	// Handle window resize
	useEffect(() => {
		const handleResize = () => {
			setScrollHeight(window.innerHeight - 280);
			setTableHeight(window.innerHeight - 260);
		};

		window.addEventListener("resize", handleResize);
		handleResize();

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Base columns definition
	const baseColumns = useMemo(
		() => [
			{
				field: "changedAt",
				headerName: "Timestamp",
				width: 180,
				renderCell: (params) => (
					<Typography
						variant="body2"
						sx={{
							fontFamily: "monospace",
							fontSize: "12px",
							color: "text.secondary",
						}}
					>
						{formatDateTime(params.value)}
					</Typography>
				),
			},
			{
				field: "tableName",
				headerName: "Entity",
				width: 150,
				renderCell: (params) => (
					<Typography
						variant="body2"
						sx={{
							fontFamily: "monospace",
							color: "secondary.main",
							fontWeight: 500,
						}}
					>
						{params.value}
					</Typography>
				),
			},
			{
				field: "recordId",
				headerName: "Record ID",
				width: 120,
				renderCell: (params) => (
					<Typography
						variant="body2"
						sx={{
							fontFamily: "monospace",
							color: "text.secondary",
						}}
					>
						{params.value || "-"}
					</Typography>
				),
			},
			{
				field: "action",
				headerName: "Action",
				width: 120,
				renderCell: (params) => (
					<Chip
						label={params.value}
						color={getActionColor(params.value)}
						size="small"
						variant="outlined"
						sx={{ fontWeight: 500 }}
					/>
				),
			},
			{
				field: "changedBy",
				headerName: "Changed By",
				width: 150,
			},
			{
				field: "description",
				headerName: "Description",
				flex: 1,
				width: 300,
				renderCell: (params) => {
					const description = formatChangeDescription(
						params.row.action,
						params.row.oldValues,
						params.row.newValues,
						params.row.tableName,
					);
					return (
						<Tooltip title={description} placement="top-start">
							<Typography
								variant="body2"
								sx={{
									color: "text.secondary",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{description}
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				field: "agencyId",
				headerName: "Agency ID",
				width: 100,
			},
		],
		[],
	);

	// Load user settings
	useEffect(() => {
		if (settingsLoaded) return;
		const settingsMap = Object.fromEntries(
			userSettings.map((s) => [s.key, s.value]),
		);

		if (settingsMap["auditLogTableColumnOrder"]) {
			setColumnsOrder(
				JSON.parse(settingsMap["auditLogTableColumnOrder"]),
			);
		}

		if (settingsMap["auditLogTableColumnSizes"]) {
			const widthArray = JSON.parse(
				settingsMap["auditLogTableColumnSizes"],
			);
			const result = widthArray.reduce((acc, curr) => {
				acc[curr.field] = curr.width;
				return acc;
			}, {});
			setColumnsSize(result);
		}

		if (settingsMap["auditLogTableColumnVisibility"]) {
			setColumnVisibilityModel(
				JSON.parse(settingsMap["auditLogTableColumnVisibility"]),
			);
		}

		if (settingsMap["auditLogTableSortModel"]) {
			const savedSortModel = JSON.parse(
				settingsMap["auditLogTableSortModel"],
			);
			if (Array.isArray(savedSortModel)) {
				const validSortModel = savedSortModel.filter((model) =>
					baseColumns.some((col) => col.field === model.field),
				);
				if (validSortModel.length > 0) {
					setSortModel(savedSortModel);
				}
			}
		}
		setSettingsLoaded(true);
	}, [userSettings, settingsLoaded, baseColumns]);

	// Build columns with order and sizes
	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			baseOrderedColumns = [...ordered, ...remaining];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	// Set default column visibility
	const allColumnFields = baseColumns.map((col) => col.field);
	useEffect(() => {
		const defaultVisibility = {};
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		// Hide oldValues and newValues by default
		defaultVisibility["oldValues"] = false;
		defaultVisibility["newValues"] = false;
		setColumnVisibilityModel((prev) => ({
			...defaultVisibility,
			...prev,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch audit logs
	useEffect(() => {
		if (!isAuthenticated || !settingsLoaded) return;

		const fetchAuditLogs = async () => {
			setLoading(true);
			try {
				const sortField = sortModel[0]?.field || "changedAt";
				const sortDirection = sortModel[0]?.sort || "desc";

				const response = await getAuditLogs(
					page,
					pageSize,
					sortField,
					sortDirection,
				);

				if (response) {
					setData(response.data || []);
					setTotal(response.total || 0);
				}
			} catch (error) {
				console.error("Error fetching audit logs:", error);
				setData([]);
				setTotal(0);
			} finally {
				setLoading(false);
			}
		};

		fetchAuditLogs();
	}, [page, pageSize, sortModel, settingsLoaded, isAuthenticated]);

	// Column order change handler
	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"auditLogTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	// Column width change handler
	const handleColumnWidthChange = useCallback(
		(params, event, details) => {
			const columnSizes = details.api.getAllColumns().map((col) => ({
				field: col.field,
				width: col.width,
			}));
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"auditLogTableColumnSizes",
						JSON.stringify(columnSizes),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	// Sort model change handler
	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(0);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"auditLogTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[dispatch, userData?.user?.ptsUserID, sortModel],
	);

	// Column visibility change handler
	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"auditLogTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	return (
		<Box>
			{/* Alert Bar */}
			<Box
				sx={{
					backgroundColor:
						themeMode === "dark"
							? "rgba(33, 150, 243, 0.1)"
							: "rgba(33, 150, 243, 0.08)",
					border: "1px solid",
					borderColor:
						themeMode === "dark"
							? "rgba(33, 150, 243, 0.3)"
							: "rgba(33, 150, 243, 0.2)",
					borderRadius: 1,
					padding: "12px 16px",
					marginX: { xs: 0, sm: 0.5, md: 1 },
					marginTop: 1,
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Typography variant="body2" color="info.main">
					🔒 All changes are immutable and timestamped. This log is
					read-only and audit-defensible.
				</Typography>
			</Box>

			{/* DataGrid Paper Container */}
			<Paper
				elevation={3}
				className={classes.paper}
				sx={{
					padding: {
						xs: theme.spacing(0.5),
						sm: theme.spacing(1),
						md: theme.spacing(1.5),
					},
					margin: {
						xs: 0,
						sm: theme.spacing(0.5),
						md: theme.spacing(1),
					},
					backgroundColor: theme.palette.background.paper,
					overflowX: "auto",
					boxSizing: "border-box",
					width: "100%",
					minWidth: 0,
					height: computedHeight,
					marginTop: theme.spacing(0.5),
				}}
			>
				<Typography sx={{ fontSize: 20, mb: 2 }}>Audit Log</Typography>

				<TableContainer
					className={classes.tableContainer}
					sx={{
						maxHeight: `${tableHeight}px`,
						overflowX: "auto",
						width: "100%",
						minWidth: 0,
					}}
				>
					<div
						style={{
							overflowX: "auto",
							width: "100%",
							minWidth: 0,
							height: `${scrollHeight}px`,
						}}
					>
						<PerfectScrollbar
							style={{
								height: "100%",
								width: "100%",
								minWidth: 0,
								overflowX: "auto",
							}}
							options={{
								suppressScrollY: false,
								suppressScrollX: false,
								wheelPropagation: true,
							}}
						>
							<DataGridPro
								rows={data}
								columns={columns}
								columnVisibilityModel={columnVisibilityModel}
								getRowId={(row) => row.id}
								className={classes.txtcolor}
								pageSize={pageSize}
								rowsPerPageOptions={rowsPerPageOptions}
								pagination
								paginationMode="server"
								sortModel={sortModel}
								sortingMode="server"
								onSortModelChange={handleSortModelChange}
								rowCount={total}
								page={page}
								onPageChange={(newPage) => setPage(newPage)}
								onPageSizeChange={(newPageSize) =>
									setPageSize(newPageSize)
								}
								loading={loading}
								disableSelectionOnClick
								disableColumnReorder={false}
								autoHeight={false}
								onColumnOrderChange={handleColumnOrderChange}
								onColumnWidthChange={handleColumnWidthChange}
								onColumnVisibilityModelChange={
									handleColumnVisibilityModelChange
								}
								sx={{
									...paginationStyle(theme, themeMode),
									width: "100%",
									minWidth: 0,
									boxSizing: "border-box",
									"& .MuiDataGrid-virtualScroller": {
										overflowX: "auto",
										width: "100%",
										minWidth: 0,
									},
								}}
							/>
						</PerfectScrollbar>
					</div>
				</TableContainer>
			</Paper>
		</Box>
	);
};

export default AuditLog;
